import {
  Checkbox,
  Collapse,
  FormControl,
  FormHelperText,
  InputLabel,
  List,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';

import {
  SELECT_PROP_TYPES,
  SELECT_AUTOCOMPLETE_PROP_TYPES,
  SELECT_MENU_PROP_TYPES,
  SELECT_MULTIPLE_PROP_TYPES,
  GROUP_ITEMS,
  GROUP_HEADER,
} from './constants';
import { ExpandLess, ExpandMore } from '@material-ui/icons';
import {
  useEffect, useRef, useState,
} from 'react';
import { truncateText } from 'utils/strings';

const GroupHeader = ({ children, onClick, isExpanded, tree = false }) => (
  <div
    onClick={onClick}
    style={{
      cursor: tree ? 'pointer' : 'default',
      padding: children ? '8px 16px' : '0px',
      margin: children ? '0px' : '0px',
      height: children ? 'auto' : '0px',
      minHeight: children ? '0px' : '0px',
      display: children ? 'block' : 'none',
    }}
  >
    {children && (
      <Typography variant="subtitle1" style={{
        fontWeight: 'bold',
      }}>
        {children}
      </Typography>
    )}
    {tree && children && (isExpanded ? <ExpandLess /> : <ExpandMore />)}
  </div>
);

const GroupItems = ({ children }) => (
  <List component="div" disablePadding style={{
    paddingLeft: '15px',
  }}>
    {children}
  </List>
);

function SelectAutocomplete(props) {
  const {
    disabled,
    error,
    events = {},
    label,
    name,
    helperText,
    size,
    value,
    onBlur,
    onChange,
    tree,
    emptyElement,
  } = props;
  let { options = [] } = props;

  const [expandedGroups, setExpandedGroups] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const selectedOptionRef = useRef(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handleScroll = (e) => {
      if (!e.target.classList.contains('MuiAutocomplete-listbox')) {
        setOpen(false);
      }
    };

    window.addEventListener('scroll', handleScroll, true);

    const modal = document.querySelector('.MuiDialog-scrollPaper');
    if (modal) {
      modal.addEventListener('scroll', handleScroll, true);
    }

    const popper = document.querySelector('.MuiAutocomplete-popper');
    if (popper) {
      popper.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      if (modal) modal.removeEventListener('scroll', handleScroll, true);
      if (popper) popper.removeEventListener('scroll', handleScroll, true);
    };
  }, [open]);

  const handleToggleGroup = (group) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [group]: !prev[group],
    }));
  };

  if (typeof options === 'function') {
    options = options();
  }

  const selectedOption = options.find(
    (option) => String(option.id) === String(value),
  );
  const hasNonEmptyValue =
    value !== undefined && value !== null && String(value) !== '';
  const isValueMismatch = hasNonEmptyValue && !selectedOption;
  const mismatchPlaceholder = '';
  const placeholder = isValueMismatch
    ? mismatchPlaceholder
    : !selectedOption && emptyElement
      ? emptyElement
      : undefined;

  useEffect(() => {
    if (tree) {
      if (searchTerm) {
        const matchingGroups = new Set();

        options.forEach((option) => {
          if (option.name.toLowerCase().includes(searchTerm.toLowerCase())) {
            matchingGroups.add(option.groupBy);
          }
        });

        const newExpandedGroups = {};
        matchingGroups.forEach((group) => {
          newExpandedGroups[group] = true;
        });

        setExpandedGroups(newExpandedGroups);
      } else {
        setExpandedGroups({});
      }
    }
  }, [searchTerm, options, tree]);

  const scrollToSelectedOption = () => {
    if (selectedOptionRef.current) {
      selectedOptionRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  };

  const handleClearValue = () => {
    const target = {
      name,
      value: '',
    };
    onChange({
      target,
    });
    if (events.onChange) {
      events.onChange(target);
    }
  };

  return (
    <Autocomplete
      disabled={disabled}
      open={open}
      onClose={() => setOpen(false)}
      onChange={(event, value) => {
        const target = {
          name,
          value: value?.id ?? '',
        };
        onChange({
          target,
        });
        if (events.onChange) {
          events.onChange(target);
        }
      }}
      onBlur={onBlur}
      onInputChange={(event, value, reason) => {
        setSearchTerm(value);
        if (reason === 'clear' || (reason === 'input' && value === '')) {
          handleClearValue();
        }
      }}
      onOpen={() => {
        setTimeout(scrollToSelectedOption, 100);
        setOpen(true);
      }}
      options={options}
      getOptionLabel={({ name }) => name}
      groupBy={({ groupBy }) => groupBy}
      value={selectedOption || null}
      clearOnEscape
      clearText="Limpiar"
      noOptionsText="No hay opciones disponibles"
      renderInput={(params) => (
        <TextField
          {...params}
          name={name}
          fullWidth
          size={size}
          error={error}
          helperText={helperText}
          label={truncateText(label, 80)}
          variant="outlined"
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            placeholder: placeholder ?? params.InputProps.placeholder,
          }}
        />
      )}
      renderOption={(option) => {
        const isSelected = String(option.id) === String(value);
        return (
          <li key={option.id} ref={isSelected ? selectedOptionRef : null}>
            {option.name}
          </li>
        );
      }}
      renderGroup={(params) => {
        if (!tree) {
          return (
            <li key={params.key}>
              <GroupHeader style={{
                pointerEvents: 'none',
              }}>
                {params.group}
              </GroupHeader>
              <GroupItems>{params.children}</GroupItems>
            </li>
          );
        }

        const { group } = params;
        const isExpanded = expandedGroups[group] || false;

        return (
          <li key={params.key}>
            <GroupHeader
              tree={tree}
              onClick={() => handleToggleGroup(group)}
              isExpanded={isExpanded}
            >
              {group}
            </GroupHeader>
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <GroupItems>{params.children}</GroupItems>
            </Collapse>
          </li>
        );
      }}
    />
  );
}

function SelectMenu(props) {
  const {
    disabled,
    emptyElement,
    loadingElements,
    error,
    events = {},
    group,
    tree,
    helperText,
    label,
    name,
    onBlur,
    onChange,
    size,
    value,
  } = props;
  let { options = [] } = props;

  if (typeof options === 'function') {
    options = options();
  }

  const groups =
    group || tree ? [...new Set(options.map(({ groupBy }) => groupBy))] : [];
  const hasNonEmptyValue =
    value !== undefined && value !== null && String(value) !== '';
  const selectedOption = (Array.isArray(options) ? options : []).find(
    (o) => String(o.id) === String(value),
  );
  const mismatchPlaceholder = '';

  return (
    <TextField
      disabled={disabled || loadingElements}
      error={error}
      fullWidth={true}
      helperText={helperText}
      InputLabelProps={{
        shrink: !!(hasNonEmptyValue && selectedOption),
      }}
      label={truncateText(label, 80)}
      name={name}
      select={true}
      SelectProps={{
        native: false,
        renderValue: (val) => {
          if ((val === undefined || val === null || val === '') && emptyElement) {
            return (
              <em style={{
                color: 'rgba(0,0,0,0.6)',
                fontStyle: 'italic',
              }}>
                {emptyElement}
              </em>
            );
          }

          const found = (Array.isArray(options) ? options : []).find(
            (o) => String(o.id) === String(val),
          );
          if (!found) {
            return (
              <em style={{
                color: 'rgba(0,0,0,0.6)',
                fontStyle: 'italic',
              }}>
                {mismatchPlaceholder}
              </em>
            );
          }
          return found.name;
        },
      }}
      size={size}
      variant="outlined"
      value={value ?? ''}
      onBlur={onBlur}
      onChange={(e) => {
        onChange(e);
        if (events.onChange) {
          events.onChange(e?.target);
        }
      }}
    >
      {loadingElements && (
        <MenuItem disabled> Cargando... </MenuItem>
      )}

      {!loadingElements && emptyElement && (
        <MenuItem value="" style={{
          color: 'rgba(0,0,0,0.6)',
          fontStyle: 'italic',
        }}>
          {emptyElement}
        </MenuItem>
      )}

      {!loadingElements && group && (
        groups.map((item, index) => {
          const opt = options.filter((_) => _.groupBy === item);

          if (!opt.length) return null;

          return [
            <MenuItem
              key={`group-${index}`}
              disabled
              style={{
                fontStyle: 'normal',
                fontWeight: 700,
              }}
            >
              {item}
            </MenuItem>,
            opt.map(({ id, name }, key) => (
              <MenuItem value={id} key={`group-${index}-opt-${key}`}>
                {name}
              </MenuItem>
            )),
          ];
        })
      )}

      {!loadingElements && !group && (
        (Array.isArray(options) ? options : []).map(({ id, name }, key) => (
          <MenuItem value={id} key={key}>
            {name}
          </MenuItem>
        ))
      )}
    </TextField>
  );
}

function SelectMultiple(props) {
  const {
    disabled,
    loadingElements,
    error,
    events = {},
    helperText,
    label,
    name,
    onChange,
    size,
  } = props;
  let { options = [], value = [] } = props;

  if (typeof value === 'string') {
    value = value.split(',').map((v) => v.trim()).filter(Boolean);
  }
  
  if (!Array.isArray(value)) {
    value = [];
  }
  
  const normalizedValue = value.map((v) => String(v));

  if (typeof options === 'function') {
    options = options();
  }

  return (
    <FormControl fullWidth size={size} variant="outlined">
      <InputLabel children={loadingElements ? 'Cargando...' : label} />
      <Select
        disabled={disabled || loadingElements}
        error={error}
        id="demo-mutiple-checkbox"
        input={<OutlinedInput label={label} />}
        multiple
        name={name}
        onChange={(e) => {
          onChange(e);
          if (events.onChange) {
            events.onChange(e?.target);
          }
        }}
        renderValue={(selected) =>
          `${selected.length} seleccionado${selected.length !== 1 ? 's' : ''}`
        }
        value={normalizedValue}
        variant="outlined"
      >
        {options.map((option, index) => (
          <MenuItem key={index} value={String(option.id)}>
            {!disabled && <Checkbox checked={normalizedValue.includes(String(option.id))} />}
            <ListItemText
              primary={option.name}
              secondary={
                option.description ? (
                  <span
                    style={{
                      color: 'rgba(0, 0, 0, 0.6)',
                      fontSize: '0.875rem',
                    }}
                  >
                    {option.description}
                  </span>
                ) : null
              }
            />
          </MenuItem>
        ))}
      </Select>
      <FormHelperText children={helperText} error />
    </FormControl>
  );
}

function SelectControl(props) {
  const {
    disabled,
    label,
    name,
    settings: {
      autocomplete = false,
      emptyElement,
      loadingElements,
      group = false,
      tree = false,
      multiple = false,
      options = [],
      limitElementToAutocomplete = 10,
    },
    events = {},
    size,
    value: rawValue,
    error,
    helperText,
    onBlur,
    onChange,
    tooltip,
  } = props;

  const resolvedOptions = typeof options === 'function' ? options() : options;

  const shouldUseAutocomplete =
    autocomplete ||
    (!multiple && Array.isArray(resolvedOptions) && resolvedOptions.length > limitElementToAutocomplete);

  const value = rawValue === null ? undefined : rawValue;

  const Control = multiple
    ? SelectMultiple
    : shouldUseAutocomplete
      ? SelectAutocomplete
      : SelectMenu;

  const controlElement = (
    <Control
      disabled={disabled}
      emptyElement={emptyElement}
      loadingElements={loadingElements}
      error={error}
      events={events}
      group={group}
      tree={tree}
      helperText={helperText}
      label={label}
      multiple={multiple}
      name={name}
      onBlur={onBlur}
      onChange={onChange}
      options={options}
      size={size}
      value={value}
    />
  );

  return tooltip ? (
    <Tooltip title={tooltip} placement="top" arrow>
      <div>{controlElement}</div>
    </Tooltip>
  ) : (
    controlElement
  );
}

GroupHeader.propTypes = GROUP_HEADER;
GroupItems.propTypes = GROUP_ITEMS;
SelectAutocomplete.propTypes = SELECT_AUTOCOMPLETE_PROP_TYPES;
SelectMenu.propTypes = SELECT_MENU_PROP_TYPES;
SelectMultiple.propTypes = SELECT_MULTIPLE_PROP_TYPES;
SelectControl.propTypes = SELECT_PROP_TYPES;

SelectControl.defaultProps = {
  disabled: false,
  settings: {
    autocomplete: false,
    group: false,
    tree: false,
    options: [],
    limitElementToAutocomplete: 10,
  },
  size: 'small',
  onChange: () => { },
  onBlur: () => { },
};

export default SelectControl;
